import { create } from "zustand";
import { ApiError, auth, me, type AuthUser } from "../lib/api";

export type AuthStatus = "checking" | "guest" | "authed";

export interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  errorMessage: string | null;
  pending: boolean;

  init: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, invite: string) => Promise<void>;
  logout: () => Promise<void>;
  /** DSGVO self-delete — irreversible. Caller is responsible for wiping local
   *  IDB and navigating away. Throws on server failure (e.g. admin 422). */
  deleteAccount: () => Promise<void>;
  /** Called by the sync manager when an API call returns 401. */
  setGuest: () => void;
  clearError: () => void;
  /** Trigger /me/verify/resend. UI surfaces the resulting toast/state. */
  requestVerifyMail: () => Promise<{ ok: true } | { ok: false; reason: "rate_limited" | "network" | "other" }>;
  /** Consume a /verify token. On success, mark the in-memory user as verified
   *  so the banner disappears without a full re-init. */
  consumeVerifyToken: (token: string) => Promise<
    | { ok: true; alreadyVerified: boolean }
    | { ok: false; reason: "invalid" | "expired" | "already_used" | "network" }
  >;
  /** Correct a typoed registration email. Only valid pre-verification. On
   *  success updates user.email locally so the banner reflects the change. */
  changeEmail: (newEmail: string) => Promise<
    | { ok: true }
    | {
        ok: false;
        reason: "invalid_email" | "email_taken" | "already_verified" | "rate_limited" | "network";
      }
  >;
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.message) {
      case "invalid_credentials":
        return "E-Mail oder Passwort falsch.";
      case "invite_invalid":
        return "Einladungscode ist ungültig oder bereits verwendet.";
      case "email_taken":
        return "Diese E-Mail ist bereits registriert.";
      case "invalid_email":
        return "Bitte gib eine gültige E-Mail-Adresse ein.";
      case "password_too_short":
        return "Passwort zu kurz — mindestens 10 Zeichen.";
      case "password_too_common":
        return "Passwort zu häufig benutzt — bitte etwas Eigenes wählen.";
      case "rate_limited":
        return "Zu viele Versuche. Bitte später erneut.";
      case "cannot_delete_self":
        return "Admin-Konten können sich nicht selbst löschen. Erst einen weiteren Admin ernennen.";
      default:
        if (err.kind === "network") return "Keine Verbindung. Versuch's nochmal.";
        if (err.kind === "server") return "Serverfehler — bitte später erneut.";
        if (err.kind === "unauthenticated") return "E-Mail oder Passwort falsch.";
        if (err.kind === "rate_limited") return "Zu viele Versuche. Bitte später erneut.";
        return err.message;
    }
  }
  return err instanceof Error ? err.message : String(err);
}

export const useAuthStore = create<AuthState>((set) => ({
  status: "checking",
  user: null,
  errorMessage: null,
  pending: false,

  async init() {
    set({ status: "checking", errorMessage: null });
    try {
      const { user } = await auth.me();
      set({ status: "authed", user });
    } catch (err) {
      if (err instanceof ApiError && err.kind === "unauthenticated") {
        set({ status: "guest", user: null });
        return;
      }
      set({ status: "guest", user: null, errorMessage: messageFor(err) });
    }
  },

  async login(email, password) {
    set({ pending: true, errorMessage: null });
    try {
      const { user } = await auth.login(email, password);
      set({ status: "authed", user, pending: false });
    } catch (err) {
      set({ pending: false, errorMessage: messageFor(err) });
      throw err;
    }
  },

  async register(email, password, invite) {
    set({ pending: true, errorMessage: null });
    try {
      const { user } = await auth.register(email, password, invite);
      set({ status: "authed", user, pending: false });
    } catch (err) {
      set({ pending: false, errorMessage: messageFor(err) });
      throw err;
    }
  },

  async logout() {
    try {
      await auth.logout();
    } catch {
      // best-effort; even if the server call fails we drop local state.
    }
    set({ status: "guest", user: null, errorMessage: null });
  },

  async deleteAccount() {
    set({ pending: true, errorMessage: null });
    try {
      await me.deleteAccount();
      set({ status: "guest", user: null, pending: false, errorMessage: null });
    } catch (err) {
      set({ pending: false, errorMessage: messageFor(err) });
      throw err;
    }
  },

  setGuest() {
    set({ status: "guest", user: null });
  },

  clearError() {
    set({ errorMessage: null });
  },

  async requestVerifyMail() {
    try {
      await me.requestVerifyMail();
      return { ok: true } as const;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.kind === "rate_limited") return { ok: false, reason: "rate_limited" } as const;
        if (err.kind === "network") return { ok: false, reason: "network" } as const;
      }
      return { ok: false, reason: "other" } as const;
    }
  },

  async changeEmail(newEmail) {
    try {
      const { user } = await me.changeEmail(newEmail);
      set({ user });
      return { ok: true } as const;
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.kind === "rate_limited") return { ok: false, reason: "rate_limited" } as const;
        if (err.kind === "network") return { ok: false, reason: "network" } as const;
        if (err.kind === "validation") {
          if (err.message === "email_taken")
            return { ok: false, reason: "email_taken" } as const;
          if (err.message === "already_verified")
            return { ok: false, reason: "already_verified" } as const;
          return { ok: false, reason: "invalid_email" } as const;
        }
      }
      return { ok: false, reason: "network" } as const;
    }
  },

  async consumeVerifyToken(token) {
    try {
      const { alreadyVerified } = await auth.verify(token);
      // Mutate user.email_verified_at locally so the banner disappears
      // immediately, without forcing a full /auth/me round-trip.
      set((prev) =>
        prev.user
          ? { user: { ...prev.user, email_verified_at: new Date().toISOString() } }
          : prev,
      );
      return { ok: true, alreadyVerified } as const;
    } catch (err) {
      if (err instanceof ApiError && err.kind === "validation") {
        const reason = err.message === "expired"
          ? "expired"
          : err.message === "already_used"
            ? "already_used"
            : "invalid";
        return { ok: false, reason } as const;
      }
      return { ok: false, reason: "network" } as const;
    }
  },
}));
